import { SharedModule } from '@app/shared/shared.module';
import { RouterModule } from '@angular/router';
import { OverlayContainer } from '@angular/cdk/overlay';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivationEnd, ActivationStart, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { routerTransition } from '@app/core/animations/router.transition';
import { UserService } from '@app/core/auth/user.service';
import { LocationService } from '@app/core/services/location.service';
import { AuthService } from '@app/core/auth/auth.service';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { delay, filter, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NIGHT_MODE_THEME, selectorSettings, SettingsRootState } from '@app/core/settings/settings.reducer';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SelectLangComponent } from '@app/shared/dialog/select-lang/select-lang.component';
import { InspektTranslateService } from '@app/core/translation/inspekt-translate.service';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { NotifyService } from '@app/core/notification/notify.service';
import { TenantService } from '@app/core/services/tenant.service';
import { TenantMenuModel } from '@app/core/models/tenant-menu.model';
import { RequirePermission } from '@app/core/enums/require-permission.enum';

@Component({
    imports: [SharedModule, RouterModule],
    selector: 'inspekt-root',
    host: { '[class]': 'componentCssClass' },
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [routerTransition]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    overlayContainer = inject(OverlayContainer);
    private store = inject<Store<SettingsRootState>>(Store);
    private router = inject(Router);
    private titleService = inject(Title);
    userService = inject(UserService);
    private authService = inject(AuthService);
    locationService = inject(LocationService);
    dialog = inject(MatDialog);
    inspektTranslate = inject(InspektTranslateService);
    dialogService = inject(DialogService);
    private cdRef = inject(ChangeDetectorRef);
    private notifyService = inject(NotifyService);
    private tenantService = inject(TenantService);

    readonly trigger = viewChild(MatMenuTrigger);
    readonly sidenav = viewChild<MatSidenav>('sidenav');
    readonly localisationButton = viewChild<any>('localisationButton');
    private unsubscribe: Subject<void> = new Subject<void>();
    public inspektRequiredPermissions = [RequirePermission.AddTenant];

    componentCssClass: string;
    locationName;
    isProd = environment.production;
    envName = environment.envName;
    version = environment.versions.app;
    navigation = [];
    navigationSideMenu = [];
    isLogout = false;
    tenantMenus: TenantMenuModel;
    adminMenu = {
        label: 'NAVIGATION.ADMINSTRATION',
        isMenu: true,
        permissions: [],
        items: []
    };

    constructor() {
        const breakpointObserver = inject(BreakpointObserver);

        this.authService.configureAuth(this.unsubscribe);
        breakpointObserver.observe([
            Breakpoints.Small,
            Breakpoints.XSmall
        ]).subscribe(result => {
            if (result.matches) {
                this.closeMatMenu();
                return;
            }
            const sidenav = this.sidenav();
            if (sidenav && sidenav.opened) {
                sidenav.close();
            }
        });
        // this language will be used as a fallback when a translation isn't found in the current language
        this.inspektTranslate.useTranslate();
    }

    private registerShowLoadingWhenNavigate() {
        this.router.events.pipe(
            filter(event => event instanceof ActivationStart))
            .pipe(
                delay(0),
                tap(() => {
                    this.dialogService.showLoader();
                })).subscribe();

        this.router.events.pipe(
            filter(event => (event instanceof NavigationEnd) || (event instanceof NavigationCancel)))
            .pipe(
                delay(0),
                tap(() => {
                    this.dialogService.hideLoader();
                })).subscribe();

        this.router.events.pipe(
            filter(event => event instanceof NavigationError))
            .pipe(
                delay(0),
                tap(() => {
                    this.dialogService.hideLoader();
                })).subscribe();
    }

    async ngOnInit() {
        this.setupStoreListeners();

        if (!environment.authEnabled) {
            return;
        }

        await this.authService.loadDiscoveryDocumentAndTryLogin();

        if (this.authService.hasValidAccessToken()) {
            this.locationName = this.locationService.currentLocation?.name ?? 'Location';
            this.setLocationHeader();

            await this.notifyService.initNotificationService();
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.registerShowLoadingWhenNavigate();
        });
    }

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
    }

    /**
     * listen to new change in Location Service
     * Current logic required `Location` text to update upon
     * location selection
     *
     * @private
     * @member of AppComponent
     */
    private setLocationHeader() {
        this.locationService.currentLocationSubject.pipe(
            takeUntil(this.unsubscribe))
            .subscribe(() => {
                this.buildNavigation();
            });
    }

    public buildNavigation() {
        this.navigation = [{ link: 'inspections', label: 'NAVIGATION.HOME' }];
        this.navigation.push({
            link: 'location',
            label: this.locationService.currentLocation?.name ?? 'NAVIGATION.LOCATION'
        });

        this.tenantService.getMenu(this.userService.currentUser.tenantId)
            .subscribe((tenantMenus: TenantMenuModel) => {

                this.tenantMenus = tenantMenus;

                // if we don't have admin menu config, then return;
                if (!this.tenantMenus) {
                    return;
                }
                const allMenus = [
                    {
                        link: 'tenants',
                        label: 'Tenants',
                        permissions: [RequirePermission.GetTenant],
                        order: 1
                    },
                    {
                        link: 'tenant-admin',
                        label: 'Tenant Admin',
                        permissions: [RequirePermission.GetTenantStyle],
                        order: 2
                    },
                    {
                        link: 'inspection-type-groups', label: 'Inspection Type Groups',
                        permissions: [RequirePermission.GetInspectionTypeGroup],
                        order: 3
                    },
                    {
                        link: 'inspection-types', label: 'Inspection Types',
                        permissions: [RequirePermission.GetInspectionType],
                        order: 4
                    },
                    {
                        link: 'category-types', label: 'Category Types',
                        permissions: [RequirePermission.GetCategoryType],
                        order: 5
                    },
                    {
                        link: 'groups', label: 'Groups',
                        permissions: [RequirePermission.GetGroup],
                        order: 6
                    },
                    {
                        link: 'roles', label: 'Roles',
                        permissions: [RequirePermission.GetRole],
                        order: 7
                    },
                    {
                        link: 'locations', label: 'Locations',
                        permissions: [RequirePermission.GetLocation],
                        order: 8
                    },
                    {
                        link: 'operation-support', label: 'Operation Support',
                        permissions: [RequirePermission.GetOperationalSupport],
                        order: 9
                    },
                    {
                        link: 'picklist-editor', label: 'Picklist Editor',
                        permissions: [RequirePermission.GetPicklistEditor],
                        order: 10
                    },
                ];

                // assign tenant permission to menu. If user have any permission, show admin menu
                this.adminMenu.permissions = tenantMenus.permissions;
                this.adminMenu = this.handleAdministrationMenuItems(allMenus, this.adminMenu);

                for (const item of this.adminMenu.items) {
                    if (this.authService.havePermission(item.permissions)) {
                        this.navigation.push(this.adminMenu);
                        break;
                    }
                }
                this.navigationSideMenu = [
                    ...this.navigation
                ];
            });
    }

    public handleAdministrationMenuItems(allMenus, adminMenu): any {
        // add menu to admin child menu base on configurations
        adminMenu.permissions.forEach(permission => {
            const menu = allMenus.find(t => t.permissions.includes(permission));
            if (menu && adminMenu.items.filter(i => i.link === menu.link).length <= 0) {
                adminMenu.items.push(menu);
            }
        });

        // sort menu
        this.adminMenu.items.sort((a, b) => (a.order > b.order) ? 1 : -1);
        return adminMenu;
    }

    private setupStoreListeners(): void {
        this.store
            .select(selectorSettings)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(settings => {
                const { theme, autoNightMode } = settings;
                const hours = new Date().getHours();
                const effectiveTheme = (autoNightMode && (hours >= 20 || hours <= 6)
                    ? NIGHT_MODE_THEME
                    : theme
                ).toLowerCase();
                this.componentCssClass = effectiveTheme;
                const classList = this.overlayContainer.getContainerElement().classList;
                classList.add(effectiveTheme);
            });

        this.router.events
            .pipe(
                takeUntil(this.unsubscribe),
                filter(event => event instanceof ActivationEnd)
            )
            .subscribe((event: ActivationEnd) => {
                let lastChild = event.snapshot;
                while (lastChild.children.length) {
                    lastChild = lastChild.children[0];
                }
                const { title } = lastChild.data;
                this.titleService.setTitle(
                    title ? `${title} - ${environment.appName}` : environment.appName
                );
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public async onLoginClick() {
        if (!environment.authEnabled) {
            return;
        }

        await this.authService.initImplicitFlow();
    }

    public async onLogoutClick() {
        this.isLogout = true;
        if (!environment.authEnabled) {
            return;
        }

        // Disconnect SignalR before clearing auth tokens so the hub teardown
        // can still send any pending server-side leave messages (CS-7.2).
        await this.notifyService.disconnect();
        await this.authService.logout();
    }

    onDeactivate() {
        document.body.scrollTop = 0;
    }

    closeMatMenu() {
        const trigger = this.trigger();
        if (trigger) {
            trigger.closeMenu();
        }
    }

    showChangeLanguageDialog() {
        this.dialog.open(SelectLangComponent, { width: '250px' });
    }
}
